import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { reportApi, feedbackApi } from '../lib/api';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useState, type FormEvent } from 'react';
import toast from 'react-hot-toast';
import LockedFeatureButton from '../components/LockedFeatureButton';
import FeatureGate from '../components/FeatureGate';
import ApprovalStatus from '../components/ApprovalStatus';
import ApprovalActions from '../components/ApprovalActions';
import AdMetadataForm from '../components/AdMetadataForm';
import ReviewComments from '../components/ReviewComments';
import ChecklistCompletion from '../components/ChecklistCompletion';
import FlagFeedbackButton from '../components/FlagFeedbackButton';
import ContentFindingsSection from '../components/fair-lending/ContentFindingsSection';
import AdDistributionPanel from '../components/AdDistributionPanel';
import { useAuthStore } from '../store/authStore';
import { useReportSSE } from '../hooks/useReportSSE';

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const user = useAuthStore((state) => state.user);
  const queryClient = useQueryClient();

  // Finding-dismissal modal state (Varo, May 2026). Captures the finding
  // we're dismissing + the reason + optional promote-to-team-exemption flag.
  const [dismissingViolation, setDismissingViolation] = useState<{
    ruleId: string;
    ruleName: string;
    ruleVersion: string | null;
    detectedText: string | null;
    contextSnippet: string | null;
  } | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissPromote, setDismissPromote] = useState(false);
  const [dismissPattern, setDismissPattern] = useState('');

  const dismissMutation = useMutation({
    mutationFn: () => reportApi.dismissFinding(id!, dismissingViolation!.ruleId, {
      reason: dismissReason,
      promoteToExemption: dismissPromote,
      exemptionPattern: dismissPromote ? (dismissPattern.trim() || dismissingViolation!.detectedText || undefined) : undefined,
      detectedText: dismissingViolation!.detectedText ?? undefined,
      contextSnippet: dismissingViolation!.contextSnippet ?? undefined,
      ruleVersion: dismissingViolation!.ruleVersion ?? undefined,
    }),
    onSuccess: () => {
      toast.success(dismissPromote ? 'Dismissed and exemption created' : 'Marked not applicable');
      setDismissingViolation(null);
      queryClient.invalidateQueries({ queryKey: ['report', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Dismiss failed'),
  });

  const undismissMutation = useMutation({
    mutationFn: (ruleId: string) => reportApi.undoDismissal(id!, ruleId),
    onSuccess: () => {
      toast.success('Dismissal undone');
      queryClient.invalidateQueries({ queryKey: ['report', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Undo failed'),
  });

  const handleDismissSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!dismissReason.trim()) {
      toast.error('Please enter a reason');
      return;
    }
    dismissMutation.mutate();
  };

  const { data: report, isLoading } = useQuery({
    queryKey: ['report', id],
    queryFn: async () => {
      const response = await reportApi.getReport(id!);
      return response.data;
    },
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'PROCESSING' ? 5000 : false;
    },
  });

  // Real-time SSE updates for in-progress reports (polling is the fallback)
  const isProcessing = report?.status === 'PENDING' || report?.status === 'PROCESSING';
  useReportSSE({ reportId: id!, enabled: !!id && !!isProcessing });

  // Fetch all reports to enable prev/next navigation
  const { data: allReportsData } = useQuery({
    queryKey: ['reports'],
    queryFn: async () => {
      const response = await reportApi.getReports();
      return response.data;
    },
  });

  // Fetch existing feedback for this report's flags
  const { data: feedbackData } = useQuery({
    queryKey: ['feedback', id],
    queryFn: async () => {
      const response = await feedbackApi.getFeedbackForReport(id!);
      return response.data;
    },
    enabled: !!id,
  });

  const getFeedbackForFlag = (flagId: string) => {
    return feedbackData?.feedback?.find((f: any) => f.flagId === flagId) || null;
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      const response = await reportApi.downloadReport(id!);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report?.upload.fileName || 'report'}_compliance_report.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Unable to download report. Please try again.');
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadExaminerReport = async () => {
    setIsDownloading(true);
    try {
      const response = await reportApi.downloadExaminerReport(id!);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report?.upload.fileName || 'report'}_examiner_report.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Examiner-ready report downloaded successfully');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Unable to download examiner report. Please try again.';
      toast.error(errorMessage);
      console.error('Examiner download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  // Find current report index
  const currentIndex = allReportsData?.reports?.findIndex((r: any) => r.id === id) ?? -1;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < (allReportsData?.reports?.length ?? 0) - 1;

  const goToPrevious = () => {
    if (hasPrevious && allReportsData?.reports) {
      navigate(`/reports/${allReportsData.reports[currentIndex - 1].id}`);
    }
  };

  const goToNext = () => {
    if (hasNext && allReportsData?.reports) {
      navigate(`/reports/${allReportsData.reports[currentIndex + 1].id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <DocumentTextIcon className="h-12 w-12 text-gray-300" />
        <h3 className="mt-3 text-sm font-medium text-gray-900">Report not found</h3>
        <p className="mt-1 text-sm text-gray-500">This report may have been deleted or you may not have access.</p>
        <button
          onClick={() => navigate('/reports')}
          className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-500"
        >
          &larr; Back to Reports
        </button>
      </div>
    );
  }

  const allChecks = [...(report.fdicChecks || []), ...(report.ncuaChecks || [])];
  const violations = report.violations || [];

  return (
    <div className="max-w-5xl mx-auto">
      {/* Navigation Bar */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/reports')}
          className="inline-flex items-center text-ocean-600 hover:text-ocean-700 font-medium"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Reports
        </button>

        <div className="flex items-center space-x-4">
          {/* Prev/Next Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevious}
              disabled={!hasPrevious}
              className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous report"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <span className="text-sm text-gray-600">
              {currentIndex >= 0 && allReportsData?.reports ? `${currentIndex + 1} of ${allReportsData.reports.length}` : ''}
            </span>
            <button
              onClick={goToNext}
              disabled={!hasNext}
              className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next report"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Download Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleDownloadReport}
              disabled={isDownloading}
              className="inline-flex items-center px-4 py-2 bg-ocean-600 text-white rounded-md hover:bg-ocean-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              {isDownloading ? 'Downloading...' : 'Download PDF'}
            </button>

            <LockedFeatureButton
              feature="examiner_export"
              onClick={handleDownloadExaminerReport}
              variant="secondary"
              disabled={isDownloading}
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              Examiner Format
            </LockedFeatureButton>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{report.upload?.fileName || 'Untitled'}</h1>
            <p className="mt-2 text-gray-600">Compliance Report</p>
          </div>
          {/* Approval Status Badge */}
          {report.approvals?.[0]?.status && (
            <ApprovalStatus status={report.approvals[0].status} />
          )}
        </div>

        {/* Report Metadata */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div>
            <p className="text-xs text-gray-500">Report ID</p>
            <p className="text-sm font-mono text-gray-900">{report.id.slice(0, 8)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Created</p>
            <p className="text-sm text-gray-900">
              {new Date(report.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">File Size</p>
            <p className="text-sm text-gray-900">
              {report.upload?.fileSize ? `${(report.upload.fileSize / 1024).toFixed(1)} KB` : 'N/A'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Processing Time</p>
            <p className="text-sm text-gray-900">
              {report.processingTime ? `${(report.processingTime / 1000).toFixed(2)}s` : 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Approval Actions */}
      {report.approvals?.[0] && (
        <ApprovalActions
          reportId={report.id}
          approvalStatus={report.approvals[0].status}
          userRole={report.teamMember?.teamRole}
          isOwner={report.team?.ownerId === user?.id}
          reviewNotes={report.approvals[0].reviewNotes}
        />
      )}

      {/* Ad Placement Details - Professional Feature */}
      <FeatureGate feature="ad_metadata" mode="lock">
        <div className="mb-8">
          <AdMetadataForm uploadId={report.uploadId} />
        </div>
      </FeatureGate>

      {/* Error State */}
      {report.status === 'ERROR' && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg p-6 mb-8 bg-red-50 border border-red-300"
        >
          <div className="flex items-center">
            <XCircleIcon className="h-12 w-12 text-red-600" />
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-red-900">Processing Error</h2>
              <p className="text-red-700">
                {report.errorMessage || 'An unexpected error occurred during compliance analysis.'}
              </p>
              <p className="text-sm text-red-600 mt-1">
                Please try re-uploading the file or contact support if the issue persists.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Pending/Processing State */}
      {(report.status === 'PENDING' || report.status === 'PROCESSING') && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg p-6 mb-8 bg-slate-50 border border-slate-200"
        >
          <div className="flex items-center">
            <div className="h-12 w-12 flex items-center justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" />
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-slate-900">
                {report.status === 'PENDING' ? 'Queued for Analysis' : 'Analyzing...'}
              </h2>
              <p className="text-slate-600">Your compliance check is being processed. This usually takes 30–60 seconds.</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Overall status - EXAMINER-DEFENSIBLE */}
      {(report.status === 'PASSED' || report.status === 'FAILED') && (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`rounded-lg p-6 mb-8 ${
          violations.length > 0 ? 'bg-yellow-50 border border-yellow-300' : 'bg-blue-50 border border-blue-200'
        }`}
      >
        <div className="flex items-center">
          {violations.length > 0 ? (
            <>
              <ExclamationTriangleIcon className="h-12 w-12 text-yellow-600" />
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-yellow-900">Flagged for Review</h2>
                <p className="text-yellow-700">{violations.length} issue(s) detected by rules engine</p>
                <p className="text-sm text-yellow-600 mt-1">Compliance officer approval required before publication</p>
              </div>
            </>
          ) : (
            <>
              <CheckCircleIcon className="h-12 w-12 text-blue-600" />
              <div className="ml-4">
                <h2 className="text-2xl font-bold text-blue-900">No Violations Detected</h2>
                <p className="text-blue-700">Rules engine found no regulatory violations</p>
                <p className="text-sm text-blue-600 mt-1">Compliance officer approval still required before publication</p>
              </div>
            </>
          )}
        </div>

        {/* Human Approval Notice - ALWAYS SHOWN */}
        <div className="mt-4 pt-4 border-t border-yellow-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="ml-3 text-sm text-gray-700">
              <strong>Important:</strong> This analysis is provided by a deterministic rules-based compliance engine. Machine learning assists with text extraction and pattern detection only.
              All advertisements must be reviewed and approved by a qualified compliance officer before publication, regardless of automated findings.
            </p>
          </div>
        </div>
      </motion.div>
      )}

      {/* Issues Detected - EXAMINER-DEFENSIBLE */}
      {violations.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Issues Detected by Rules Engine</h3>
            <p className="text-sm text-gray-600 mt-1">
              These items require compliance officer review under applicable regulations
            </p>
          </div>
          <div className="p-6 space-y-4">
            {violations.map((violation: any) => {
              // Dismissed > exempted > satisfied > active severity. The
              // border + badge follow the precedence of the strongest
              // dispositive state.
              const isDismissed = !!violation.dismissed;
              const isExempted = !violation.dismissed && !!violation.exemptedByExemption;
              const isSatisfied = !violation.dismissed && !violation.exemptedByExemption && !!violation.satisfiedByLandingPage;
              const isLive = !isDismissed && !isExempted && !isSatisfied;
              return (
              <div key={violation.ruleId || violation.name} className={`border-l-4 pl-4 py-3 rounded-r ${
                isDismissed ? 'border-gray-400 bg-gray-50 opacity-90' :
                isExempted  ? 'border-purple-500 bg-purple-50/40' :
                isSatisfied ? 'border-green-500 bg-green-50' :
                violation.severity === 'CRITICAL' ? 'border-red-500 bg-red-50' :
                violation.severity === 'HIGH' ? 'border-orange-500 bg-orange-50' :
                violation.severity === 'MEDIUM' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
              }`}>
                {/* Rule ID and Severity */}
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">
                      {violation.ruleId}: {violation.ruleName}
                    </p>
                    {violation.regulation && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        {violation.regulation}
                      </p>
                    )}
                  </div>
                  {isDismissed ? (
                    <span className="ml-4 px-2 py-1 text-xs font-semibold rounded bg-gray-300 text-gray-800 inline-flex items-center gap-1">
                      Dismissed
                    </span>
                  ) : isExempted ? (
                    <span className="ml-4 px-2 py-1 text-xs font-semibold rounded bg-purple-200 text-purple-900 inline-flex items-center gap-1">
                      Exempted
                    </span>
                  ) : isSatisfied ? (
                    <span className="ml-4 px-2 py-1 text-xs font-semibold rounded bg-green-200 text-green-800 inline-flex items-center gap-1">
                      <CheckCircleIcon className="h-3 w-3" />
                      Satisfied
                    </span>
                  ) : (
                    <span className={`ml-4 px-2 py-1 text-xs font-semibold rounded ${
                      violation.severity === 'CRITICAL' ? 'bg-red-200 text-red-800' :
                      violation.severity === 'HIGH' ? 'bg-orange-200 text-orange-800' :
                      violation.severity === 'MEDIUM' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'
                    }`}>
                      {violation.severity}
                    </span>
                  )}
                </div>

                {/* Dismissal note */}
                {isDismissed && (
                  <div className="mt-2 p-2 bg-white border border-gray-300 rounded">
                    <p className="text-xs text-gray-700">
                      <strong>Marked not applicable.</strong>{' '}
                      Reason: {violation.dismissalReason}
                      {violation.dismissedAt && (
                        <span className="text-gray-500"> · {new Date(violation.dismissedAt).toLocaleDateString()}</span>
                      )}
                    </p>
                    <button
                      type="button"
                      onClick={() => undismissMutation.mutate(violation.ruleId)}
                      disabled={undismissMutation.isPending}
                      className="mt-1 text-xs font-medium text-teal-700 hover:text-teal-900 underline-offset-2 hover:underline disabled:opacity-50"
                    >
                      Undo dismissal
                    </button>
                  </div>
                )}

                {/* Exemption note */}
                {isExempted && (
                  <div className="mt-2 p-2 bg-white border border-purple-200 rounded">
                    <p className="text-xs text-purple-900">
                      <strong>Auto-dismissed by exemption rule:</strong>{' '}
                      {violation.exemptionRuleName}.{' '}
                      <Link to="/custom-rules" className="underline hover:text-purple-700">Manage exemptions</Link>
                    </p>
                  </div>
                )}

                {/* Landing-page satisfaction note */}
                {isSatisfied && (
                  <div className="mt-2 p-2 bg-white border border-green-200 rounded">
                    <p className="text-xs text-green-800">
                      <strong>Disclosure verified on linked landing page.</strong>{' '}
                      Required text &ldquo;{violation.landingPageMatchedKeyword}&rdquo; found at{' '}
                      <a
                        href={violation.landingPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-green-900 break-all"
                      >
                        {violation.landingPageUrl}
                      </a>
                    </p>
                  </div>
                )}

                {/* Description */}
                <p className="text-gray-700 text-sm mt-2">{violation.description || violation.category}</p>

                {/* Detected Text */}
                {violation.detectedText && (
                  <div className="mt-2 p-2 bg-white border border-gray-200 rounded">
                    <p className="text-xs text-gray-600 font-medium">Detected Text:</p>
                    <p className="text-sm text-gray-800 mt-1">{violation.detectedText}</p>
                  </div>
                )}

                {/* Required Action */}
                {violation.requiredAction && (
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-xs text-blue-800 font-medium">Required Action:</p>
                    <p className="text-sm text-blue-900 mt-1">{violation.requiredAction}</p>
                  </div>
                )}

                {/* Suggestion (Legacy) */}
                {violation.suggestion && !violation.requiredAction && (
                  <p className="text-sm text-gray-600 mt-2">
                    <strong>Suggestion:</strong> {violation.suggestion}
                  </p>
                )}

                {/* Rules Engine Attribution */}
                <p className="text-xs text-gray-500 mt-2 italic">
                  Detected by: Rules Engine v{violation.ruleVersion || '1.0.0'}
                  {violation.assistedBy && ` (AI-assisted: ${violation.assistedBy})`}
                </p>

                {/* Action row */}
                <div className="mt-3 flex items-center gap-3 flex-wrap">
                  {/* Flag Feedback (existing) — for telling Clarid the rule itself looks wrong */}
                  <FlagFeedbackButton
                    reportId={report.id}
                    flagId={violation.ruleId}
                    flagName={violation.ruleName}
                    existingFeedback={getFeedbackForFlag(violation.ruleId)}
                  />
                  {/* Mark not applicable — for "this finding doesn't apply to our products" */}
                  {isLive && (
                    <button
                      type="button"
                      onClick={() => {
                        setDismissingViolation({
                          ruleId: violation.ruleId,
                          ruleName: violation.ruleName,
                          ruleVersion: violation.ruleVersion ?? null,
                          detectedText: violation.detectedText ?? null,
                          contextSnippet: violation.contextSnippet ?? null,
                        });
                        setDismissReason('');
                        setDismissPromote(false);
                        setDismissPattern(violation.detectedText ?? '');
                      }}
                      className="text-xs font-medium text-gray-700 hover:text-gray-900 underline-offset-2 hover:underline"
                    >
                      Mark not applicable
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          {/* Compliance Officer Action Required */}
          <div className="px-6 py-4 bg-yellow-50 border-t border-yellow-200">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Compliance Officer Review Required</p>
                <p className="text-sm text-yellow-700 mt-1">
                  A qualified compliance officer must review these detected issues and approve or reject this advertisement for publication.
                  The rules engine provides guidance, but human judgment is required for final compliance determination.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* UDAAP Risk Scoring - Professional Feature */}
      {report.udaapUnfairScore !== null && report.udaapUnfairScore !== undefined && (
        <FeatureGate feature="udaap_scoring" mode="lock">
          <div className="bg-white rounded-lg shadow mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">UDAAP Pattern Detection</h3>
              <p className="text-sm text-gray-600 mt-1">
                Rules-based detection of unfair, deceptive, or abusive language patterns (AI-assisted)
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                {/* Unfair Practices Score */}
                <div className={`rounded-lg p-4 ${
                  (report.udaapUnfairScore || 0) <= 30 ? 'bg-green-50' :
                  (report.udaapUnfairScore || 0) <= 60 ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <div className="text-sm text-gray-600">Unfair Practices</div>
                  <div className={`text-2xl font-bold ${
                    (report.udaapUnfairScore || 0) <= 30 ? 'text-green-600' :
                    (report.udaapUnfairScore || 0) <= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {(report.udaapUnfairScore || 0) <= 30 ? 'Low Risk' :
                     (report.udaapUnfairScore || 0) <= 60 ? 'Medium Risk' : 'High Risk'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Score: {report.udaapUnfairScore || 0}/100</div>
                </div>

                {/* Deceptive Language Score */}
                <div className={`rounded-lg p-4 ${
                  (report.udaapDeceptiveScore || 0) <= 30 ? 'bg-green-50' :
                  (report.udaapDeceptiveScore || 0) <= 60 ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <div className="text-sm text-gray-600">Deceptive Language</div>
                  <div className={`text-2xl font-bold ${
                    (report.udaapDeceptiveScore || 0) <= 30 ? 'text-green-600' :
                    (report.udaapDeceptiveScore || 0) <= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {(report.udaapDeceptiveScore || 0) <= 30 ? 'Low Risk' :
                     (report.udaapDeceptiveScore || 0) <= 60 ? 'Medium Risk' : 'High Risk'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Score: {report.udaapDeceptiveScore || 0}/100</div>
                </div>

                {/* Abusive Tactics Score */}
                <div className={`rounded-lg p-4 ${
                  (report.udaapAbusiveScore || 0) <= 30 ? 'bg-green-50' :
                  (report.udaapAbusiveScore || 0) <= 60 ? 'bg-yellow-50' : 'bg-red-50'
                }`}>
                  <div className="text-sm text-gray-600">Abusive Tactics</div>
                  <div className={`text-2xl font-bold ${
                    (report.udaapAbusiveScore || 0) <= 30 ? 'text-green-600' :
                    (report.udaapAbusiveScore || 0) <= 60 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {(report.udaapAbusiveScore || 0) <= 30 ? 'Low Risk' :
                     (report.udaapAbusiveScore || 0) <= 60 ? 'Medium Risk' : 'High Risk'}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Score: {report.udaapAbusiveScore || 0}/100</div>
                </div>
              </div>

              {/* Flagged Phrases */}
              {report.udaapFlaggedPhrases && Array.isArray(report.udaapFlaggedPhrases) && report.udaapFlaggedPhrases.length > 0 && (
                <div className="border-t pt-4 mb-4">
                  <h4 className="font-medium text-gray-900 mb-3">Flagged Phrases</h4>
                  <div className="space-y-3">
                    {report.udaapFlaggedPhrases.map((phrase: any, index: number) => (
                      <div key={`udaap-${phrase.category}-${index}`} className={`border-l-4 pl-4 py-2 ${
                        phrase.severity === 'high' ? 'border-red-500 bg-red-50' :
                        phrase.severity === 'medium' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
                      }`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">"{phrase.text}"</p>
                            <p className="text-sm text-gray-600 mt-1">{phrase.explanation}</p>
                            <p className="text-xs text-gray-500 mt-1 italic">{phrase.regulatoryBasis}</p>
                          </div>
                          <span className={`ml-4 px-2 py-1 text-xs font-semibold rounded ${
                            phrase.severity === 'high' ? 'bg-red-200 text-red-800' :
                            phrase.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' : 'bg-blue-200 text-blue-800'
                          }`}>
                            {phrase.severity.toUpperCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* UDAAP Recommendations */}
              {report.udaapRecommendations && Array.isArray(report.udaapRecommendations) && report.udaapRecommendations.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">UDAAP Recommendations</h4>
                  <ul className="space-y-2">
                    {report.udaapRecommendations.map((recommendation: string, index: number) => (
                      <li key={`rec-${index}-${recommendation.slice(0, 30)}`} className="flex items-start text-sm text-gray-700">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* No findings message */}
              {(!report.udaapFlaggedPhrases || report.udaapFlaggedPhrases.length === 0) &&
               (!report.udaapRecommendations || report.udaapRecommendations.length === 0) && (
                <div className="border-t pt-4">
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    <span className="text-sm font-medium">No significant UDAAP risks detected</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </FeatureGate>
      )}

      {/* Fair Lending Content Screening — Enterprise only */}
      <FeatureGate feature="fair_lending" mode="hide">
        <ContentFindingsSection reportId={id!} />
      </FeatureGate>

      {/* Distribution mapping capture — where/when this piece ran (backlog #3) */}
      {report.teamId && (
        <FeatureGate feature="fair_lending" mode="hide">
          <AdDistributionPanel teamId={report.teamId} reportId={report.id} />
        </FeatureGate>
      )}

      {/* AI-Assisted Recommendations */}
      {report.suggestions && report.suggestions.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Compliance Recommendations</h3>
            <p className="text-sm text-gray-600 mt-1">
              These suggestions are for informational purposes only and do not replace compliance officer review
            </p>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {report.suggestions.map((suggestion: string, index: number) => (
                <li key={`suggestion-${index}-${suggestion.slice(0, 30)}`} className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{suggestion}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600 italic">
                Note: These recommendations are generated by a rules-based engine and are provided for guidance only. Final compliance decisions must be made by a qualified compliance officer.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Editorial review (advisory — spelling/grammar/clarity; NOT a compliance finding) */}
      {report.editorialFindings?.findings?.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Editorial Review</h3>
              <p className="text-sm text-gray-600 mt-1">
                Spelling, grammar &amp; clarity suggestions — advisory only, does not affect compliance status.
              </p>
            </div>
            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
              {report.editorialFindings.findings.length} suggestion{report.editorialFindings.findings.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {report.editorialFindings.findings.map((f: any, i: number) => (
                <li key={`editorial-${i}`} className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600">
                    {f.type}
                  </span>
                  <span className="text-sm text-gray-700">
                    <span className="line-through text-gray-400">{f.excerpt}</span>
                    {f.suggestion && <> → <span className="font-medium text-gray-900">{f.suggestion}</span></>}
                    {f.explanation && <span className="block text-xs text-gray-500 mt-0.5">{f.explanation}</span>}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500 italic">
              {report.editorialFindings.advisoryNote}
            </p>
          </div>
        </div>
      )}

      {/* All checks */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Compliance Checks</h3>
        </div>
        <div className="p-6 space-y-4">
          {allChecks.length === 0 ? (
            <div className="py-6 text-center">
              <CheckCircleIcon className="mx-auto h-10 w-10 text-green-500" />
              <p className="mt-2 text-sm font-medium text-gray-900">No compliance checks recorded</p>
              <p className="mt-1 text-xs text-gray-500">
                This report passed without flagging any individual rule. Older "No Issues" reports
                may not have per-rule check details — newer scans surface every rule evaluated.
              </p>
            </div>
          ) : (
            allChecks.map((check: any, index: number) => (
              <div
                key={check.ruleId || `check-${index}`}
                className={`border-l-4 pl-4 py-2 ${
                  check.status === 'pass' ? 'border-green-500' : 'border-red-500'
                }`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-gray-900">
                    {check.ruleId}: {check.ruleName}
                  </p>
                  {check.status === 'pass' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{check.requirement}</p>
                {check.finding && (
                  <p className="text-sm text-gray-700 mt-1">
                    <strong>Finding:</strong> {check.finding}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Extracted text */}
      {report.extractedText && (
        <div className="bg-white rounded-lg shadow mt-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Extracted Text</h3>
          </div>
          <div className="p-6">
            <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded border border-gray-200">
              {report.extractedText}
            </pre>
          </div>
        </div>
      )}

      {/* Compliance Checklists - Enterprise Feature */}
      <FeatureGate feature="compliance_checklists" mode="hide">
        <div className="mb-8">
          <ChecklistCompletion reportId={report.id} />
        </div>
      </FeatureGate>

      {/* Review Comments - Professional Feature */}
      <FeatureGate feature="review_comments" mode="lock">
        <div className="mb-8">
          <ReviewComments reportId={report.id} />
        </div>
      </FeatureGate>

      {/* Audit Trail & Technical Details */}
      <div className="bg-white rounded-lg shadow mt-8">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Technical Details & Audit Trail</h3>
          <p className="text-xs text-gray-500 mt-1">Information for compliance officers and examiners</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Scan Information */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm">Scan Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Report ID:</span>
                  <span className="font-mono text-gray-900">{report.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    report.status === 'PASSED' ? 'text-blue-600' :
                    report.status === 'FAILED' ? 'text-yellow-600' :
                    report.status === 'ERROR' ? 'text-red-600' :
                    report.status === 'PROCESSING' ? 'text-teal-600' :
                    'text-gray-600'
                  }`}>
                    {report.status === 'PASSED' ? 'No Violations Detected' :
                     report.status === 'FAILED' ? 'Flagged for Review' :
                     report.status === 'ERROR' ? 'Processing Error' :
                     report.status === 'PROCESSING' ? 'Analyzing...' :
                     report.status === 'PENDING' ? 'Queued' :
                     report.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Processing Time:</span>
                  <span className="text-gray-900">
                    {report.processingTime ? `${(report.processingTime / 1000).toFixed(2)} seconds` : 'N/A'}
                  </span>
                </div>
                {report.ruleVersion && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ruleset Version:</span>
                    <span className="text-gray-900">{report.ruleVersion.version}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Information */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm">Upload Information</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">File Name:</span>
                  <span className="text-gray-900 truncate ml-2">{report.upload?.fileName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">File Type:</span>
                  <span className="text-gray-900">{report.upload?.fileType || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">File Size:</span>
                  <span className="text-gray-900">
                    {report.upload?.fileSize ? `${(report.upload.fileSize / 1024).toFixed(2)} KB` : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">MIME Type:</span>
                  <span className="text-gray-900 text-xs">{report.upload?.mimeType || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm">Timestamps</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Created:</span>
                  <span className="text-gray-900">
                    {new Date(report.createdAt).toLocaleString('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Last Updated:</span>
                  <span className="text-gray-900">
                    {new Date(report.updatedAt).toLocaleString('en-US', {
                      dateStyle: 'medium',
                      timeStyle: 'short'
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* Compliance Analysis */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 text-sm">Compliance Analysis</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Violations Detected:</span>
                  <span className="font-medium text-gray-900">{violations.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Checks:</span>
                  <span className="text-gray-900">{allChecks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Requires Approval:</span>
                  <span className="text-yellow-600 font-medium">Yes (Always)</span>
                </div>
                {report.shareEnabled && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sharing:</span>
                    <span className="text-green-600 font-medium">Enabled</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Detection Elements */}
          {report.detectedElements && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-gray-900 text-sm mb-3">Detected Ad Elements</h4>
              <div className="bg-gray-50 p-4 rounded border border-gray-200">
                <pre className="text-xs text-gray-700 whitespace-pre-wrap overflow-auto max-h-48">
                  {JSON.stringify(report.detectedElements, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── Mark-not-applicable modal ─── */}
      {dismissingViolation && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setDismissingViolation(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Mark not applicable</h2>
            <p className="text-sm text-gray-600 mb-4">
              <span className="font-mono text-gray-900">{dismissingViolation.ruleId}</span> · {dismissingViolation.ruleName}
            </p>

            {dismissingViolation.detectedText && (
              <div className="mb-4 p-2 bg-gray-50 border border-gray-200 rounded text-xs">
                <p className="text-gray-500 font-medium mb-0.5">Detected text:</p>
                <p className="text-gray-800">{dismissingViolation.detectedText}</p>
              </div>
            )}

            <form onSubmit={handleDismissSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                  Why isn't this applicable?
                </label>
                <textarea
                  value={dismissReason}
                  onChange={e => setDismissReason(e.target.value)}
                  rows={3}
                  required
                  placeholder="e.g., this product doesn't offer credit, so the APR disclosure rule doesn't apply"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Saved on the audit trail. Examiners will see this reason next to the dismissed finding.
                </p>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dismissPromote}
                    onChange={e => setDismissPromote(e.target.checked)}
                    className="mt-0.5 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm text-gray-700">
                    <strong>Always dismiss future findings matching this pattern</strong>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Creates a team-level exemption rule. Future ads where this rule
                      ({dismissingViolation.ruleId}) fires on text matching the pattern below
                      will auto-dismiss with a link back to this exemption.
                    </span>
                  </span>
                </label>
                {dismissPromote && (
                  <div className="mt-2 ml-6">
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                      Match pattern (substring or comma-separated alternatives)
                    </label>
                    <input
                      type="text"
                      value={dismissPattern}
                      onChange={e => setDismissPattern(e.target.value)}
                      placeholder={dismissingViolation.detectedText ?? 'Enter a substring to match'}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2 justify-end">
                <button type="button" onClick={() => setDismissingViolation(null)} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!dismissReason.trim() || dismissMutation.isPending}
                  className="bg-gray-700 hover:bg-gray-800 disabled:opacity-50 text-white px-4 py-2 rounded-md text-sm font-semibold"
                >
                  {dismissMutation.isPending ? 'Saving…' : 'Mark not applicable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
